import { useState, useEffect } from 'react';
import api from '../../../api/axios';
import type { TimelineEvent } from '../types/timeline.types';

interface UseInlineEntryProps {
  caseId: string;
  headers: any;
  fetchTimelineEvents: () => Promise<TimelineEvent[]>;
  setError: (error: string | null) => void;
}

export const useInlineEntry = ({
  caseId,
  headers,
  fetchTimelineEvents,
  setError
}: UseInlineEntryProps) => {
  // Inline entry type toggle state
  const [inlineEntryType, setInlineEntryType] = useState<'log' | 'task' | 'pending'>('log');
  const [inlineEditorContent, setInlineEditorContent] = useState<string>('');
  const [newlyCreatedEventId, setNewlyCreatedEventId] = useState<string | null>(null);
  const [editorKey, setEditorKey] = useState<number>(0); // Used to force editor remount

  // Inline log state
  const [inlineLogDate, setInlineLogDate] = useState<string>('');

  // Inline task state
  const [inlineTaskTitle, setInlineTaskTitle] = useState<string>('');
  const [inlineTaskPriority, setInlineTaskPriority] = useState<string>('medium');
  const [inlineTaskAssignedTo, setInlineTaskAssignedTo] = useState<string>('');
  const [inlineTaskDueDate, setInlineTaskDueDate] = useState<string>('');

  // Shared state
  const [inlineFiles, setInlineFiles] = useState<File[]>([]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);

  // Pending tasks view state
  const [viewMode, setViewMode] = useState<'timeline' | 'pending'>('timeline');
  const [pendingTasks, setPendingTasks] = useState<any[]>([]);
  const [loadingPendingTasks, setLoadingPendingTasks] = useState(false);
  const [pendingTasksFetched, setPendingTasksFetched] = useState(false);

  // Initialize inline log date to current datetime
  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const datetimeLocal = `${year}-${month}-${day}T${hours}:${minutes}`;
    setInlineLogDate(datetimeLocal);
  }, []);

  // Fetch available users for task assignment
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersResponse = await api.get('/users/', { headers });
        const users = usersResponse.data.results || usersResponse.data || [];
        setAvailableUsers(users);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };

    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch pending tasks when pending tab is selected (with caching)
  useEffect(() => {
    const fetchPendingTasks = async () => {
      if (inlineEntryType !== 'pending') {
        return;
      }

      // Skip if already fetched (cache)
      if (pendingTasksFetched) {
        return;
      }

      setLoadingPendingTasks(true);
      try {
        // Fetch all tasks for this case
        const response = await api.get(`/tasks/?case=${caseId}`, { headers });
        const tasks = response.data.results || response.data || [];

        // Filter to only pending and in_progress tasks
        const pendingAndInProgressTasks = tasks.filter((task: any) =>
          task.status === 'pending' || task.status === 'in_progress'
        );

        // Enrich tasks with assigned user names
        const enrichedTasks = pendingAndInProgressTasks.map((task: any) => {
          const assignedUser = availableUsers.find(u => u.id === task.assigned_to);
          return {
            ...task,
            assigned_to_name: assignedUser
              ? (assignedUser.first_name && assignedUser.last_name
                ? `${assignedUser.first_name} ${assignedUser.last_name}`
                : assignedUser.email)
              : null
          };
        });

        // Sort tasks by due date (earliest first), then by created_at
        enrichedTasks.sort((a: any, b: any) => {
          // Tasks with due dates come before tasks without
          if (a.due_date && !b.due_date) return -1;
          if (!a.due_date && b.due_date) return 1;

          // If both have due dates, sort by due date
          if (a.due_date && b.due_date) {
            const dateComparison = new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
            if (dateComparison !== 0) return dateComparison;
          }

          // If same due date (or both null), sort by created_at (oldest first)
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        });

        setPendingTasks(enrichedTasks);
        setPendingTasksFetched(true); // Mark as fetched
      } catch (error) {
        console.error('Failed to fetch pending tasks:', error);
        setError('Failed to load pending tasks');
      } finally {
        setLoadingPendingTasks(false);
      }
    };

    fetchPendingTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inlineEntryType, caseId, availableUsers, pendingTasksFetched]);

  // Handle transcription completion from audio recorder
  const handleTranscriptionComplete = (transcription: string, audioBlob: Blob) => {
    // Parse the current editor content as TipTap JSON
    let currentDoc;
    try {
      if (inlineEditorContent.trim()) {
        currentDoc = JSON.parse(inlineEditorContent);
      } else {
        // Empty editor - create empty doc structure
        currentDoc = { type: 'doc', content: [] };
      }
    } catch (e) {
      // If parsing fails, assume it's an empty document
      currentDoc = { type: 'doc', content: [] };
    }

    // Create new paragraph nodes with the transcription text
    const transcriptionLines = transcription.split('\n');
    const newParagraphs = transcriptionLines.map(line => ({
      type: 'paragraph',
      content: line.trim() ? [{ type: 'text', text: line }] : undefined
    }));

    // Append to existing content
    if (!currentDoc.content) {
      currentDoc.content = [];
    }
    currentDoc.content.push(...newParagraphs);

    // Set the new content
    setInlineEditorContent(JSON.stringify(currentDoc));

    // Force editor remount to display new content
    setEditorKey(prev => prev + 1);

    // Convert blob to File and add to attachments
    const audioFile = new File([audioBlob], `voice-note-${Date.now()}.webm`, {
      type: 'audio/webm'
    });
    setInlineFiles([...inlineFiles, audioFile]);
  };

  // Handle transcription error
  const handleTranscriptionError = (errorMessage: string) => {
    setError(errorMessage);
  };

  // Handle inline entry creation (both case logs and tasks)
  const handleCreateInlineLog = async () => {
    if (!caseId) return;

    try {
      let response;
      let createdId: string | undefined;

      if (inlineEntryType === 'task') {
        // Validate task title
        if (!inlineTaskTitle.trim()) {
          setError('Task title is required');
          return;
        }

        // Ensure description is a string (could be object or string from editor)
        const descriptionToSend = typeof inlineEditorContent === 'string'
          ? inlineEditorContent
          : JSON.stringify(inlineEditorContent);

        // Create task
        if (inlineFiles.length > 0) {
          const formData = new FormData();
          formData.append('title', inlineTaskTitle.trim());
          formData.append('description', descriptionToSend);
          formData.append('priority', inlineTaskPriority);
          formData.append('case', caseId);

          if (inlineTaskDueDate) {
            formData.append('due_date', new Date(inlineTaskDueDate).toISOString());
          }

          if (inlineTaskAssignedTo) {
            formData.append('assigned_to', inlineTaskAssignedTo);
          }

          // Add files
          inlineFiles.forEach((file) => {
            formData.append('attachment_files', file);
          });

          response = await api.post('/tasks/', formData, {
            headers: {
              ...headers,
              'Content-Type': 'multipart/form-data',
            },
          });
        } else {
          // Use JSON payload when no files
          const payload: any = {
            title: inlineTaskTitle.trim(),
            description: descriptionToSend,
            priority: inlineTaskPriority,
            case: caseId,
          };

          if (inlineTaskDueDate) {
            payload.due_date = new Date(inlineTaskDueDate).toISOString();
          }

          if (inlineTaskAssignedTo) {
            payload.assigned_to = inlineTaskAssignedTo;
          }

          response = await api.post('/tasks/', payload, { headers });
        }

        createdId = response.data?.id;

        // Invalidate pending tasks cache so new task appears
        setPendingTasksFetched(false);

        // Reset task fields
        setInlineTaskTitle('');
        setInlineTaskPriority('medium');
        setInlineTaskAssignedTo('');
        setInlineTaskDueDate('');
      } else {
        // Create case log
        if (!inlineEditorContent.trim()) return;

        const formData = new FormData();
        formData.append('content', inlineEditorContent.trim());
        // Use the selected date or fallback to current date if not set
        const logDateToUse = inlineLogDate ? new Date(inlineLogDate).toISOString() : new Date().toISOString();
        formData.append('log_date', logDateToUse);
        formData.append('tags', '');

        // Add files if any
        inlineFiles.forEach((file) => {
          formData.append('attachment_files', file);
        });

        response = await api.post(`/cases/${caseId}/log/`, formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data',
          },
        });

        createdId = response.data?.id;
      }

      // Refresh timeline to show the new entry
      const refreshedEvents = await fetchTimelineEvents();

      // Find the timeline event that corresponds to this entry
      if (createdId && refreshedEvents) {
        const newEvent = refreshedEvents.find((e: TimelineEvent) => e.related_object_id === createdId);
        if (newEvent) {
          setNewlyCreatedEventId(newEvent.id);
        } else if (refreshedEvents.length > 0) {
          setNewlyCreatedEventId(refreshedEvents[0].id);
        }
      }

      // Clear the animation after 2 seconds
      setTimeout(() => {
        setNewlyCreatedEventId(null);
      }, 2000);

      // Reset common fields
      setInlineEditorContent('');
      setInlineFiles([]);
      setEditorKey(prev => prev + 1);
      setError(null);
    } catch (error) {
      console.error(`Failed to create ${inlineEntryType}:`, error);
      setError(`Failed to create ${inlineEntryType === 'task' ? 'task' : 'case log entry'}`);
    }
  };

  return {
    // State
    inlineEntryType,
    inlineEditorContent,
    newlyCreatedEventId,
    editorKey,
    inlineLogDate,
    inlineTaskTitle,
    inlineTaskPriority,
    inlineTaskAssignedTo,
    inlineTaskDueDate,
    inlineFiles,
    availableUsers,
    viewMode,
    pendingTasks,
    loadingPendingTasks,
    pendingTasksFetched,

    // Setters
    setInlineEntryType,
    setInlineEditorContent,
    setEditorKey,
    setInlineLogDate,
    setInlineTaskTitle,
    setInlineTaskPriority,
    setInlineTaskAssignedTo,
    setInlineTaskDueDate,
    setInlineFiles,
    setViewMode,
    setPendingTasks,
    setPendingTasksFetched,

    // Functions
    handleTranscriptionComplete,
    handleTranscriptionError,
    handleCreateInlineLog,
  };
};
